import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    OnChangeFn,
    Row,
    RowSelectionState,
    SortingState,
    TableMeta,
    useReactTable,
    type VisibilityState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useRef, useMemo, useEffect, Fragment } from "react";
import { Spinner } from "@heroui/react";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowDownWideShort,
    faArrowUpShortWide,
} from "@fortawesome/free-solid-svg-icons";

function TableV2<TData>({
    meta,
    data,
    columns,
    sorting,
    setSorting,
    manualSorting,
    isLoading,
    onSelectionChange,
    onGlobalFilterChange,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    showScrollbar = false,
    onRowClick,
    onRowMouseEnter,
    pinnedColumns,
    columnVisibility,
    setColumnVisibility,
    showEndMessage = false,
    paddingBottom,
    estimateSize = 60,
    rowSelection,
    onRowSelectionChange,
    enableMultiRowSelection = true,
    getRowId,
}: {
    meta?: TableMeta<TData>;
    data: TData[];
    columns: ColumnDef<TData>[];
    sorting: SortingState;
    setSorting: (
        updater: SortingState | ((old: SortingState) => SortingState),
    ) => void;
    manualSorting?: boolean;
    isLoading?: boolean;
    onSelectionChange?: (selectedData: TData[]) => void;
    onGlobalFilterChange?: (value: string) => void;
    hasNextPage?: boolean;
    fetchNextPage?: () => void;
    isFetchingNextPage?: boolean;
    showScrollbar?: boolean;
    onRowClick?: (row: TData) => void;
    onRowMouseEnter?: (row: TData) => void;
    pinnedColumns?: string[];
    columnVisibility?: Record<string, boolean>;
    setColumnVisibility?: (
        updater: VisibilityState | ((old: VisibilityState) => VisibilityState),
    ) => void;
    showEndMessage?: boolean;
    paddingBottom?: string;
    estimateSize?: number;
    rowSelection?: RowSelectionState;
    onRowSelectionChange?: OnChangeFn<RowSelectionState>;
    enableMultiRowSelection?: boolean;
    getRowId?: (originalRow: TData, index: number) => string;
}) {
    const table = useReactTable<TData>({
        columns,
        data: data,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        state: {
            sorting,
            columnVisibility: columnVisibility ?? {},
            ...(rowSelection !== undefined ? { rowSelection } : {}),
        },
        onSortingChange: setSorting,
        onColumnVisibilityChange: setColumnVisibility,
        ...(onRowSelectionChange
            ? {
                onRowSelectionChange,
                enableMultiRowSelection,
                getRowId,
            }
            : {}),
        manualSorting: manualSorting || false,
        onGlobalFilterChange: onGlobalFilterChange,
        globalFilterFn: (row, columnId, filterValue) => {
            const value = row.getValue(columnId);
            return String(value)
                .toLowerCase()
                .includes(filterValue.toLowerCase());
        },
        meta: meta,
        initialState: {
            columnPinning: {
                left:
                    (pinnedColumns?.length ?? 0 > 0)
                        ? pinnedColumns
                        : undefined,
            },
        },
    });

    const { rows } = table.getRowModel();

    const ref = useRef<HTMLDivElement>(null);

    const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
        count: rows.length,
        estimateSize: () => estimateSize,
        getScrollElement: () => ref.current,
        overscan: 50,
    });

    const virtualItems = rowVirtualizer.getVirtualItems();

    useEffect(() => {
        if (!fetchNextPage || !hasNextPage) return;

        const lastItem = virtualItems.at(-1);
        if (!lastItem) return;

        if (
            lastItem.index >= data.length - 1 &&
            hasNextPage &&
            !isFetchingNextPage
        ) {
            fetchNextPage();
        }
    }, [
        virtualItems,
        data.length,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
    ]);

    const selectedRows = table.getSelectedRowModel();
    const selectedFields = useMemo(
        () => selectedRows.rows.map((row) => row.original),
        [selectedRows],
    );
    useEffect(() => {
        if (onSelectionChange) {
            onSelectionChange(selectedFields);
        }
    }, [selectedFields, onSelectionChange]);

    return (
        <div
            ref={ref}
            className={classNames(
                "h-full overflow-y-scroll overflow-x-scroll bg-white w-full min-h-[150px]",
                {
                    "scrollbar-hide": !showScrollbar,
                },
            )}
            style={{
                paddingBottom,
            }}
        >
            <div
                style={{
                    height: `calc(${rowVirtualizer.getTotalSize()}px + 40px)`,
                    position: "relative",
                }}
            >
                {isLoading && (
                    <div className="absolute inset-0 bg-gray-100/60 z-20 w-full flex items-start justify-center">
                        <Spinner
                            size="lg"
                            color="primary"
                            label="Loading..."
                            className="opacity-100 pt-4"
                        />
                    </div>
                )}
                <div
                    className="sticky top-0 z-10 flex flex-col w-full bg-white border-b border-gray-200 min-w-fit"
                    role="rowgroup"
                >
                    {table.getHeaderGroups().map((group) => (
                        <div
                            className="flex flex-row items-center justify-start"
                            key={group.id}
                        >
                            {group.headers.map((header) => {
                                const isPinned = header.column.getIsPinned();
                                const pinnedStyles = isPinned
                                    ? {
                                        position: "sticky" as const,
                                        left: header.column.getStart("left"),
                                        zIndex: 11,
                                        backgroundColor: "white",
                                    }
                                    : {};

                                return (
                                    <div
                                        key={header.id}
                                        aria-label={
                                            header.column.columnDef.header?.toString() ??
                                            "column header"
                                        }
                                        style={{
                                            width: header.column.getSize(),
                                            minWidth: 0,
                                            ...pinnedStyles,
                                        }}
                                        className="py-1 font-extrabold"
                                    >
                                        {header.isPlaceholder ? null : (
                                            <div
                                                onClick={header.column.getToggleSortingHandler()}
                                                style={{
                                                    width: "100%",
                                                }}
                                                className={classNames({
                                                    "rounded-lg py-1 pr-1 text-start w-full flex items-center text-sm h-full":
                                                        true,
                                                    "cursor-pointer":
                                                        header.column.getCanSort(),
                                                })}
                                            >
                                                {flexRender(
                                                    header.column.columnDef
                                                        .header,
                                                    header.getContext(),
                                                )}
                                                {header.column.getCanSort() &&
                                                    header.column.getIsSorted() && (
                                                        <FontAwesomeIcon
                                                            className="ml-2 text-primary-500"
                                                            icon={
                                                                header.column.getIsSorted() ===
                                                                    "asc"
                                                                    ? faArrowUpShortWide
                                                                    : faArrowDownWideShort
                                                            }
                                                            size="sm"
                                                        />
                                                    )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>

                <div className="relative pt-[40px] min-w-fit">
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                        const row = rows[virtualRow.index] as Row<TData>;
                        return (
                            <Fragment key={row.id}>
                                <div
                                    data-index={virtualRow.index}
                                    key={row.id}
                                    style={{
                                        height: `${virtualRow.size}px`,
                                        transform: `translateY(${virtualRow.start}px)`,
                                        display: "flex",
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                    }}
                                    className={classNames(
                                        "border-b items-center border-gray-200",
                                        {
                                            "bg-primary-50":
                                                row.getIsSelected(),
                                            "cursor-pointer":
                                                (onRowClick || onRowSelectionChange) &&
                                                !row.getIsSelected(),
                                        },
                                    )}
                                    role="row"
                                    onMouseEnter={() => {
                                        onRowMouseEnter?.(row.original);
                                    }}
                                    onClick={() => {
                                        if (onRowSelectionChange) {
                                            row.toggleSelected();
                                        }
                                        if (onRowClick) {
                                            onRowClick(row.original);
                                        }
                                    }}
                                >
                                    {row.getVisibleCells().map((cell) => {
                                        const isPinned =
                                            cell.column.getIsPinned();
                                        const pinnedStyles = isPinned
                                            ? {
                                                position: "sticky" as const,
                                                left: cell.column.getStart(
                                                    "left",
                                                ),
                                                zIndex: 1,
                                            }
                                            : {};

                                        return (
                                            <div
                                                key={cell.id}
                                                style={{
                                                    width: cell.column.getSize(),
                                                    minWidth: 0,
                                                    overflow: "hidden",
                                                    ...pinnedStyles,
                                                }}
                                                className={classNames({
                                                    "bg-gray-50 hover:bg-gray-100":
                                                        isPinned && (onRowClick || onRowSelectionChange) && !row.getIsSelected(),
                                                    "bg-white":
                                                        isPinned && !onRowClick && !onRowSelectionChange && !row.getIsSelected(),
                                                })}
                                                role="cell"
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext(),
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </Fragment>
                        );
                    })}

                    {data?.length == 0 && !isLoading && (
                        <div className="absolute h-[100px] translate-y-full w-full bottom-0 flex items-center justify-center z-40">
                            <p className="text-gray-500">No data found</p>
                        </div>
                    )}
                </div>

                {/* Infinite scroll indicators */}
                {isFetchingNextPage && (
                    <div className="absolute h-[100px] translate-y-full w-full bottom-0 flex items-center justify-center z-40">
                        <Spinner label="Loading..." color="primary" />
                    </div>
                )}
                {showEndMessage && data?.length > 0 && (
                    <div className="absolute h-[100px] translate-y-full w-full bottom-0 flex items-center justify-center z-40">
                        <p className="text-gray-500">End of data</p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default TableV2;
